import { IconCircleInfoOutline18 as Info } from "nucleo-ui-outline-18"
import { z } from "zod"

import { primaryAddress } from "@productlane/lib/src/email-settings"
import {
	Button,
	CopyButton,
	Form,
	FormCheckbox,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import type { EmailInboxSettingsProps } from "../types"
import { api } from "@/lib/api"
import { SettingsContainer } from "../../settings-switches/settings-container"

const ForwardSchema = z.object({
	isForwardingEnabled: z.boolean(),
})

export function ForwardStep({ workspace }: EmailInboxSettingsProps) {
	const address = primaryAddress(workspace.emailSettings)
	const utils = api.useUtils()
	const form = useZodForm({
		schema: ForwardSchema,
		defaultValues: {
			isForwardingEnabled: address?.isForwardingSetup ?? false,
		},
	})
	const updateMutation = api.mail.updateEmailIntegration.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
			setTimeout(() => {
				const settingsScrollContainer = document.getElementById(
					"settings-scroll-container",
				)
				if (settingsScrollContainer) {
					settingsScrollContainer.scrollTo({
						top: settingsScrollContainer.scrollHeight,
						behavior: "smooth",
					})
				}
			}, 100)
		},
	})
	return (
		<Form
			form={form}
			onSubmit={async (values) => {
				if (!values.isForwardingEnabled) {
					form.setError("isForwardingEnabled", { message: "Required" })
					return
				}
				await updateMutation.mutateAsync({
					isForwardingSetup: true,
				})
			}}
		>
			<div className="mb-2 font-semibold text-primary">Receiving emails</div>
			<div className="mb-4 text-sm text-secondary">
				Please forward emails from {address?.primaryEmail} to this address.
			</div>
			<SettingsContainer className="divide-y-0">
				<div className="mb-2 flex items-center justify-between">
					<div className="text-sm font-medium">
						Forward inbound emails to this address:
					</div>
					<Button
						Icon={Info}
						variant="link"
						onClick={() => {
							// @ts-expect-error Global widget calls
							// eslint-disable-next-line
							window.Productlane.openDocs(
								"6c07a98b-6728-4f77-b0a8-4ce1525175ba",
							)
						}}
					>
						How to set up forwarding
					</Button>
				</div>
				<div className="mb-8 flex items-center gap-4 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
					<div className="flex-1 truncate font-mono text-sm">
						{address?.brandedInboundEmailAddress}
					</div>
					<CopyButton
						value={address?.brandedInboundEmailAddress ?? ""}
						className="!text-tertiary hover:!text-primary"
					/>
				</div>
				<div className="flex items-center justify-between">
					<FormCheckbox
						label="I have added forwarding"
						name="isForwardingEnabled"
					/>
					<SubmitButton>Save and continue</SubmitButton>
				</div>
			</SettingsContainer>
		</Form>
	)
}
